import styles from './Button.module.css';

type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
type ButtonSize = 'medium' | 'large' | 'label';
type ButtonColour = 'black' | 'blue' | 'green' | 'grey' | 'white';

type ButtonSharedProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  colour?: ButtonColour;
  iconStart?: React.ReactNode;
  iconEnd?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

type ButtonAsAnchor = ButtonSharedProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonSharedProps> & {
    href: string;
  };

type ButtonAsButton = ButtonSharedProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonSharedProps> & {
    href?: undefined;
  };

export type ButtonProps = ButtonAsAnchor | ButtonAsButton;

function buildClassName(
  variant: ButtonVariant,
  size: ButtonSize,
  colour: ButtonColour,
  className?: string,
): string {
  return [
    styles.button,
    styles[`variant${variant.charAt(0).toUpperCase()}${variant.slice(1)}` as keyof typeof styles],
    styles[`size${size.charAt(0).toUpperCase()}${size.slice(1)}` as keyof typeof styles],
    styles[`colour${colour.charAt(0).toUpperCase()}${colour.slice(1)}` as keyof typeof styles],
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

function ButtonContent({
  iconStart,
  iconEnd,
  children,
}: Pick<ButtonSharedProps, 'iconStart' | 'iconEnd' | 'children'>) {
  return (
    <>
      {iconStart ? <span className={styles.icon}>{iconStart}</span> : null}
      <span className={styles.label}>{children}</span>
      {iconEnd ? <span className={styles.icon}>{iconEnd}</span> : null}
    </>
  );
}

export function Button({
  variant = 'primary',
  size = 'medium',
  colour = 'blue',
  iconStart,
  iconEnd,
  children,
  className,
  ...rest
}: ButtonProps) {
  const classes = buildClassName(variant, size, colour, className);

  if ('href' in rest && rest.href) {
    const { href, ...anchorRest } = rest;
    return (
      <a href={href} className={classes} {...anchorRest}>
        <ButtonContent iconStart={iconStart} iconEnd={iconEnd}>
          {children}
        </ButtonContent>
      </a>
    );
  }

  const { disabled, type = 'button', ...buttonRest } = rest as ButtonAsButton;

  return (
    <button type={type} disabled={disabled} className={classes} {...buttonRest}>
      <ButtonContent iconStart={iconStart} iconEnd={iconEnd}>
        {children}
      </ButtonContent>
    </button>
  );
}
